/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {
    Box,
    Flex,
    SimpleGrid,
    Text,
    Button,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {
    COLOR_REGROUPMENT_ID_BLACK,
    COLOR_REGROUPMENT_ID_WHITE,
    cssColorGroups
} from '../../../constants'
import {useIntl} from 'react-intl'
import {useProductList} from '../util/product-list-context'

import {
    ADD_FILTER_HIT_COUNT,
    REMOVE_FILTER_HIT_COUNT
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {REFINEMENT_LEGEND} from '../../product-list'

/** A mapping of color refinement attribute number value to their corresponding color string value. */
const colorMap = {
    '00010': 'white',
    '00020': 'beige',
    '00030': 'yellow',
    '00040': 'orange',
    '00050': 'pink',
    '00060': 'red',
    '00070': 'blue',
    '00080': 'green',
    '00090': 'purple',
    '00100': 'brown',
    '00110': 'grey',
    '00120': 'black',
    '00130': 'miscellaneous'
}

/**
 * ColorRefinements is a React functional component that renders a list of color filter options.
 *
 * This component is memoized using React.memo, meaning it will only re-render when its props change.
 * The props are used solely for data representation, as the component's actions are derived from the
 * useProductList hook, which provides the toggleFilter function to handle filter interactions.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.filter - The filter object containing color options and metadata.
 * @param {Array} props.selectedFilters - Array of currently selected filter values.
 *
 * @returns {JSX.Element|null} A fieldset containing a grid of color options.
 */
const ColorRefinements = ({filter, selectedFilters}) => {
    const intl = useIntl()
    const {toggleFilter} = useProductList()
    const styles = useMultiStyleConfig('SwatchGroup', {
        variant: 'buttonWithChip',
        disabled: false
    })

    return (
        filter.values?.length > 0 && (
            <Box as="fieldset">
                {/* ADA legend */}
                <legend className="sr-only">
                    {intl.formatMessage(REFINEMENT_LEGEND, {label: filter.label})}
                </legend>

                <SimpleGrid
                    as="ul"
                    columns={[4, null, 6]}
                    spacingY={5}
                    mt={1}
                    listStyleType={'none'}
                >
                    {filter.values.map((value, idx) => {
                        const isSelected = selectedFilters.includes(value.value)
                        const bgColorName = colorMap[value.value?.toLowerCase()] || ''
                        const bgColor = cssColorGroups[bgColorName] || 'transparent'

                        // Don't display refinements with no results, unless we got there by selecting too
                        // many refinements
                        if (value.hitCount === 0 && !isSelected) return

                        return (
                            <Flex as="li" key={`color-item-${value.value}`}>
                                <Button
                                    {...styles.swatch}
                                    onClick={() =>
                                        toggleFilter(value, filter.attributeId, isSelected)
                                    }
                                    aria-checked={isSelected}
                                    borderColor={isSelected ? 'fullBlack' : 'gray'}
                                    bgColor={isSelected ? 'fullBlack' : 'transparent'}
                                    zIndex={isSelected ? 1 : 0}
                                    variant="outline"
                                    aria-label={intl.formatMessage(
                                        isSelected ? REMOVE_FILTER_HIT_COUNT : ADD_FILTER_HIT_COUNT,
                                        value
                                    )}
                                >
                                    <Box
                                        {...styles.chip}
                                        marginRight={0}
                                        backgroundRepeat="no-repeat"
                                        backgroundSize="cover"
                                        {...(bgColorName === 'miscellaneous'
                                            ? {background: bgColor}
                                            : {backgroundColor: bgColor})}
                                        borderWidth={
                                            value.value === COLOR_REGROUPMENT_ID_WHITE ||
                                            (value.value === COLOR_REGROUPMENT_ID_BLACK &&
                                                isSelected)
                                                ? '1px'
                                                : 0
                                        }
                                        borderColor={
                                            value.value === COLOR_REGROUPMENT_ID_BLACK && isSelected
                                                ? 'white'
                                                : 'gray'
                                        }
                                    />
                                    <Text
                                        variant={'bodySmall'}
                                        {...styles.text}
                                        color={isSelected ? 'white' : 'darkGray'}
                                        aria-hidden="true" // avoid redundant readout since swatch has aria label
                                    >
                                        {value.label}
                                    </Text>
                                </Button>
                            </Flex>
                        )
                    })}
                </SimpleGrid>
            </Box>
        )
    )
}

ColorRefinements.propTypes = {
    filter: PropTypes.object,
    selectedFilters: PropTypes.array
}

// Memoized ColorRefinements to prevent unnecessary re-renders.
// Use case: accordion open / close with ColorRefinements
export default React.memo(ColorRefinements)
